import { useEffect, useState, useRef } from "react";
import { Search, Plus, ShoppingCart, FileText, ChevronLeft, ChevronRight, Eye, X, Trash2, Layers } from "lucide-react";
import api from "../services/api";
import PurchaseForm from "../components/PurchaseForm";
import Swal from "sweetalert2";

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  // ✅ ESTADOS PARA EL MODAL DE DETALLE
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // ✅ ESTADOS DE PAGINACIÓN
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  // ✅ Búsqueda en el backend
  const fetchPurchases = async (page = 1, searchTerm = search) => {
    try {
      setLoading(true);
      const res = await api.get(`/purchases/paginated?page=${page}&search=${searchTerm}`);
      
      if (res.data.data) {
        setPurchases(res.data.data);
        setCurrentPage(res.data.current_page);
        setTotalPages(res.data.last_page);
        setTotal(res.data.total);
        setFrom(res.data.from);
        setTo(res.data.to);
      } else {
        setPurchases(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error("Error cargando compras", error);
      setPurchases([]);
      Swal.fire("Error", "No se pudieron cargar las compras", "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Función para cargar el detalle de una compra
  const fetchPurchaseDetail = async (purchaseId) => {
    try {
      setLoadingDetail(true);
      const res = await api.get(`/purchases/${purchaseId}`);
      setSelectedPurchase(res.data.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Error cargando detalle", error);
      Swal.fire("Error", "No se pudo cargar el detalle de la compra", "error");
    } finally {
      setLoadingDetail(false);
    }
  };

  // 🆕 HELPER: Obtener nombre completo del producto con presentación
  const getNombreProducto = (detail) => {
    const nombreBase = detail.product?.nombre || "Sin nombre";
    
    if (detail.presentation) {
      return (
        <div>
          <strong>{nombreBase}</strong>
          <div style={{ 
            fontSize: "13px", 
            color: "#38b2ac", 
            marginTop: "4px",
            display: "flex",
            alignItems: "center",
            gap: "4px"
          }}>
            <Layers size={12} />
            {detail.presentation.descripcion}
            {detail.presentation.tipo_presentacion === 'variante' && (
              <span style={{
                fontSize: "10px",
                padding: "2px 6px",
                background: "#805ad5",
                color: "white",
                borderRadius: "4px",
                fontWeight: 600,
                marginLeft: "4px"
              }}>
                Variante
              </span>
            )}
            {detail.presentation.tipo_presentacion === 'empaque' && (
              <span style={{
                fontSize: "10px",
                padding: "2px 6px",
                background: "#38b2ac",
                color: "white",
                borderRadius: "4px",
                fontWeight: 600,
                marginLeft: "4px"
              }}>
                Empaque
              </span>
            )}
          </div>
        </div>
      );
    }
    
    return <strong>{nombreBase}</strong>;
  };

  // ✅ Función para eliminar una compra
  const handleDelete = async (purchase) => {
    const result = await Swal.fire({
      title: "¿Eliminar esta compra?",
      html: `
        <div style="text-align: left; margin-top: 16px;">
          <p style="margin-bottom: 8px;"><strong>Proveedor:</strong> ${purchase.supplier?.nombre}</p>
          <p style="margin-bottom: 8px;"><strong>N° Factura:</strong> ${purchase.numero_factura_proveedor}</p>
          <p style="margin-bottom: 8px;"><strong>Total:</strong> $${Number(purchase.monto_final).toFixed(2)}</p>
          <p style="margin-top: 16px; color: #e53e3e; font-weight: 600;">⚠️ Esta acción revertirá el stock de todos los productos de esta compra.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e53e3e",
      cancelButtonColor: "#718096",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/purchases/${purchase.id}`);
        
        Swal.fire({
          title: "¡Eliminada!",
          text: "La compra ha sido eliminada y el stock ha sido revertido.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        // Recargar la lista
        fetchPurchases(currentPage, search);
      } catch (error) {
        console.error("Error eliminando compra", error);
        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar la compra",
          "error"
        );
      }
    }
  };

  useEffect(() => {
    fetchPurchases();

    // autofocus al buscador al cargar
    setTimeout(() => {
      searchRef.current?.focus();
    }, 100);
  }, []);

  // ✅ Función para cambiar página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchPurchases(newPage, search);
    }
  };

  // ✅ Manejar búsqueda con debounce
  const handleSearch = (value) => {
    setSearch(value);
    
    // Limpiar timeout anterior
    clearTimeout(window.searchTimeout);
    
    // Buscar después de 500ms de que el usuario dejó de escribir
    window.searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchPurchases(1, value);
    }, 500);
  };

  // ✅ Cerrar modal
  const closeModal = () => {
    setShowDetailModal(false);
    setSelectedPurchase(null);
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* CARD PRINCIPAL */}
      <div className="card">
        {/* HEADER */}
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <ShoppingCart size={28} color="#667eea" />
            <h1 className="card-title">Gestión de Compras</h1>
          </div>

          {!showForm && (
            <button
              className="btn btn-success"
              onClick={() => setShowForm(true)}
            >
              <Plus size={18} />
              Nueva Compra
            </button>
          )}
        </div>

        {/* BODY */}
        <div className="card-body">
          {/* FORMULARIO */}
          {showForm && (
            <PurchaseForm
              onSaved={() => {
                fetchPurchases(currentPage, search);
                setShowForm(false);
                setTimeout(() => searchRef.current?.focus(), 50);
              }}
              onCancel={() => {
                setShowForm(false);
                setTimeout(() => searchRef.current?.focus(), 50);
              }}
            />
          )}

          {/* BUSCADOR */}
          {!showForm && (
            <div className="form-group" style={{ position: "relative" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a0aec0",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por proveedor o número de factura..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  ref={searchRef}
                  style={{ paddingLeft: "44px" }}
                />
              </div>
              {search && (
                <small style={{ color: "#718096", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  🔍 Buscando en todas las compras...
                </small>
              )}
            </div>
          )}

          {/* TABLA */}
          {!showForm && (
            <>
              {loading ? (
                <div className="text-center" style={{ padding: "40px" }}>
                  <div className="spinner" style={{ margin: "0 auto" }}></div>
                  <p style={{ marginTop: "16px", color: "#718096" }}>
                    Cargando compras...
                  </p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Proveedor</th>
                        <th>Fecha</th>
                        <th>N° Factura</th>
                        <th style={{ textAlign: "right" }}>Total</th>
                        <th style={{ textAlign: "center", width: "150px" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.length === 0 ? (
                        <tr>
                          <td colSpan="5">
                            <div
                              className="text-center"
                              style={{ padding: "40px 20px" }}
                            >
                              <FileText
                                size={48}
                                color="#cbd5e0"
                                style={{ marginBottom: "16px" }}
                              />
                              <p
                                style={{
                                  color: "#a0aec0",
                                  fontSize: "16px",
                                  margin: 0,
                                }}
                              >
                                {search
                                  ? "No se encontraron compras con ese criterio"
                                  : "No hay compras registradas"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        purchases.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <span style={{ fontWeight: 600 }}>
                                {p.supplier?.nombre}
                              </span>
                            </td>
                            <td>{p.fecha_compra}</td>
                            <td>
                              <strong style={{ fontWeight: 600 }}>
                                {p.numero_factura_proveedor}
                              </strong>
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: 700,
                                color: "#48bb78",
                              }}
                            >
                              ${Number(p.monto_final).toFixed(2)}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => fetchPurchaseDetail(p.id)}
                                  title="Ver detalle"
                                  disabled={loadingDetail}
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(p)}
                                  title="Eliminar compra"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ✅ PAGINACIÓN */}
              {totalPages > 1 && (
                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <span style={{ padding: "6px 12px", color: "#718096", fontSize: "14px" }}>
                    Página {currentPage} de {totalPages}
                  </span>

                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {/* RESUMEN */}
              {purchases.length > 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f7fafc",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#718096", fontSize: "14px" }}>
                    Mostrando {from} - {to} de {total} compras
                  </span>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "18px",
                      color: "#2d3748",
                    }}
                  >
                    Total en página: $
                    {Array.isArray(purchases) ? purchases
                      .reduce((sum, p) => sum + Number(p.monto_final), 0)
                      .toFixed(2) : '0.00'}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ✅ MODAL DE DETALLE */}
      {showDetailModal && selectedPurchase && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={closeModal}
        >
          <div
            className="card"
            style={{
              maxWidth: "900px",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              margin: 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER DEL MODAL */}
            <div className="card-header" style={{ 
              position: "sticky", 
              top: 0, 
              zIndex: 10,
              background: "white",
              borderBottom: "2px solid #e2e8f0"
            }}>
              <div className="d-flex align-items-center gap-2">
                <FileText size={24} color="#667eea" />
                <h2 className="card-title">Detalle de Compra</h2>
              </div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={closeModal}
              >
                <X size={18} />
                Cerrar
              </button>
            </div>

            {/* BODY DEL MODAL */}
            <div className="card-body">
              {/* INFORMACIÓN GENERAL */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "16px",
                marginBottom: "24px",
                padding: "20px",
                background: "#f7fafc",
                borderRadius: "10px",
              }}>
                <div>
                  <label style={{ fontSize: "12px", color: "#718096", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                    Proveedor
                  </label>
                  <p style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>
                    {selectedPurchase.supplier?.nombre}
                  </p>
                </div>

                <div>
                  <label style={{ fontSize: "12px", color: "#718096", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                    Fecha de Compra
                  </label>
                  <p style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>
                    {selectedPurchase.fecha_compra}
                  </p>
                </div>

                <div>
                  <label style={{ fontSize: "12px", color: "#718096", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                    N° Factura Proveedor
                  </label>
                  <p style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#667eea" }}>
                    {selectedPurchase.numero_factura_proveedor}
                  </p>
                </div>
              </div>

              {/* SEPARADOR */}
              <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

              {/* PRODUCTOS */}
              <div>
                <h3 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>
                  Productos
                </h3>

                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th style={{ textAlign: "center" }}>Cantidad</th>
                        <th style={{ textAlign: "right" }}>P. Unitario (sin IVA)</th>
                        <th style={{ textAlign: "right" }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPurchase.purchase_details?.map((detail) => (
                        <tr key={detail.id}>
                          <td>
                            {getNombreProducto(detail)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {detail.cantidad}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            ${Number(detail.costo_unitario).toFixed(2)}
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>
                            ${Number(detail.subtotal_linea).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* SEPARADOR */}
              <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

              {/* TOTALES */}
              <div style={{
                background: "#f7fafc",
                padding: "20px",
                borderRadius: "10px"
              }}>
                <div style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  gap: "12px",
                  maxWidth: "400px",
                  marginLeft: "auto"
                }}>
                  {/* Subtotal */}
                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: "16px", fontWeight: 500 }}>
                      Subtotal (sin IVA):
                    </span>
                    <span style={{ fontSize: "16px", fontWeight: 600 }}>
                      ${Number(selectedPurchase.subtotal).toFixed(2)}
                    </span>
                  </div>

                  {/* IVA */}
                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: "16px", fontWeight: 500 }}>
                      IVA (13%):
                    </span>
                    <span style={{ fontSize: "16px", fontWeight: 600 }}>
                      ${Number(selectedPurchase.iva_total).toFixed(2)}
                    </span>
                  </div>

                  {/* Línea divisoria */}
                  <div style={{ borderTop: "2px solid #cbd5e0", margin: "4px 0" }}></div>

                  {/* Total */}
                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: "18px", fontWeight: 700 }}>
                      Total de la Compra:
                    </span>
                    <span style={{
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#48bb78"
                    }}>
                      ${Number(selectedPurchase.monto_final).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}